import { create } from "zustand";
import { devtools } from "zustand/middleware";
import { sliceResetFns } from "./utils";
import { NOTIFICATIONS_EVENT_TYPE } from "./websocketStore";

/** The live-analytics WS event a notification comes from. */
export type LiveNotificationType =
  | NOTIFICATIONS_EVENT_TYPE.NewMaxConcurrentViewers
  | NOTIFICATIONS_EVENT_TYPE.ViewMilestoneReached
  | NOTIFICATIONS_EVENT_TYPE.ProjectTrending
  | NOTIFICATIONS_EVENT_TYPE.ChatbotMessageAnswered
  | NOTIFICATIONS_EVENT_TYPE.QuizSubmitted;

export interface LiveNotification {
  id: string;
  type: LiveNotificationType;
  projectId: string;
  /** Peak / milestone value, or quiz score (%); absent for trending, chatbot and quizzes without a score. */
  count?: number;
  timestamp: number;
  /** False until the user marks notifications as read. */
  seen: boolean;
}

// Drop repeat events for the same (type, project, value) within this window so
// bursts across subscribed projects don't flood the list.
const DEDUPE_WINDOW_MS = 10_000;
// Keep the list bounded — only the most recent matter.
const MAX_NOTIFICATIONS = 30;

type LiveNotificationsState = {
  /** Organization the list belongs to; another organization starts a new list. */
  organizationId: string | null;
  notifications: LiveNotification[];
};

type LiveNotificationsStore = LiveNotificationsState & {
  add: (
    organizationId: string,
    type: LiveNotificationType,
    projectId: string,
    count?: number,
    dedupe?: boolean
  ) => void;
  markRead: (id: string) => void;
  markAllRead: () => void;
  reset: () => void;
};

const initialState: LiveNotificationsState = {
  organizationId: null,
  notifications: [],
};

// Last accepted time per dedupe key; not state, nothing renders it.
const lastAcceptedAt = new Map<string, number>();

// Lives above the routes so the list survives navigation; empty after a reload.
export const useLiveNotificationsStore = create<LiveNotificationsStore>()(
  devtools(
    (set, get) => {
      const reset = () => {
        lastAcceptedAt.clear();
        set(initialState, false, "reset");
      };
      // Cleared on logout with the other slices.
      sliceResetFns.add(reset);

      return {
        ...initialState,

        add: (organizationId, type, projectId, count, dedupe = true) => {
          if (get().organizationId !== organizationId) {
            lastAcceptedAt.clear();
          }

          const key = `${type}:${projectId}:${count ?? ""}`;
          const now = Date.now();
          if (dedupe) {
            const last = lastAcceptedAt.get(key) ?? 0;
            if (now - last < DEDUPE_WINDOW_MS) return;
            lastAcceptedAt.set(key, now);
          }

          const item: LiveNotification = {
            id: `${key}:${now}:${Math.random().toString(36).slice(2, 8)}`,
            type,
            projectId,
            count,
            timestamp: now,
            seen: false,
          };
          set(
            (state) => ({
              organizationId,
              notifications: [
                item,
                ...(state.organizationId === organizationId
                  ? state.notifications
                  : []),
              ].slice(0, MAX_NOTIFICATIONS),
            }),
            false,
            `add [${type}]`
          );
        },

        markRead: (id) =>
          set(
            (state) =>
              state.notifications.some((n) => n.id === id && !n.seen)
                ? {
                    notifications: state.notifications.map((n) =>
                      n.id === id ? { ...n, seen: true } : n
                    ),
                  }
                : state,
            false,
            "markRead"
          ),

        markAllRead: () =>
          set(
            (state) =>
              state.notifications.some((n) => !n.seen)
                ? {
                    notifications: state.notifications.map((n) =>
                      n.seen ? n : { ...n, seen: true }
                    ),
                  }
                : state,
            false,
            "markAllRead"
          ),

        reset,
      };
    },
    { name: "LiveNotificationsStore" }
  )
);
